import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Flex,
    Grid,
    GridItem,
    IconButton,
    Text,
} from "@chakra-ui/react";
import {
    ChevronLeft,
    ChevronRight,
    CalendarDays,
} from "lucide-react";

const PRIMARY_COLOR = "#2453FF";

const WEEK_DAYS = [
    "Su",
    "Mo",
    "Tu",
    "We",
    "Th",
    "Fr",
    "Sa",
];

const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const pad = value =>
    String(value).padStart(2, "0");

const formatDate = date => {
    if (!date) return "";

    return `${date.getFullYear()}-${pad(
        date.getMonth() + 1
    )}-${pad(date.getDate())}`;
};

const parseDate = value => {
    if (!value) return null;

    const [year, month, day] =
        value.split("-").map(Number);

    if (!year || !month || !day) {
        return null;
    }

    return new Date(
        year,
        month - 1,
        day
    );
};

const isSameDay = (a, b) => {
    if (!a || !b) return false;

    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
};

const startOfDay = date => {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
};

const MogomaDatePicker = ({
    value,
    onChange,
    placeholder = "Select date",
    minDate,
}) => {
    const selectedDate = parseDate(value);

    const today = useMemo(
        () => startOfDay(new Date()),
        []
    );

    const minimumDate = minDate
        ? startOfDay(parseDate(minDate))
        : null;

    const [isOpen, setIsOpen] =
        useState(false);

    const [visibleMonth, setVisibleMonth] =
        useState(
            selectedDate ||
                today
        );

    const calendarDays = useMemo(() => {
        const year =
            visibleMonth.getFullYear();

        const month =
            visibleMonth.getMonth();

        const firstDay = new Date(
            year,
            month,
            1
        ).getDay();

        const daysInMonth = new Date(
            year,
            month + 1,
            0
        ).getDate();

        const previousMonthDays =
            new Date(
                year,
                month,
                0
            ).getDate();

        const days = [];

        for (
            let i = firstDay - 1;
            i >= 0;
            i--
        ) {
            days.push({
                date: new Date(
                    year,
                    month - 1,
                    previousMonthDays - i
                ),
                currentMonth: false,
            });
        }

        for (
            let day = 1;
            day <= daysInMonth;
            day++
        ) {
            days.push({
                date: new Date(
                    year,
                    month,
                    day
                ),
                currentMonth: true,
            });
        }

        let nextDay = 1;

        while (days.length < 42) {
            days.push({
                date: new Date(
                    year,
                    month + 1,
                    nextDay++
                ),
                currentMonth: false,
            });
        }

        return days;
    }, [visibleMonth]);

    const isDisabled = date => {
        if (!minimumDate) {
            return false;
        }

        return (
            startOfDay(date) <
            minimumDate
        );
    };

    const handleSelect = date => {
        if (isDisabled(date)) {
            return;
        }

        onChange(formatDate(date));
        setIsOpen(false);
    };

    const changeMonth = amount => {
        setVisibleMonth(
            previous => {
                return new Date(
                    previous.getFullYear(),
                    previous.getMonth() +
                        amount,
                    1
                );
            }
        );
    };

    const displayValue = selectedDate
        ? selectedDate.toLocaleDateString(
              "en-GB",
              {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
              }
          )
        : "";

    return (
        <Box position="relative">
            <Button
                type="button"
                width="100%"
                height="44px"
                justifyContent="flex-start"
                gap={3}
                px={3}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="10px"
                fontWeight="400"
                color={
                    displayValue
                        ? "gray.800"
                        : "gray.400"
                }
                _hover={{
                    borderColor:
                        PRIMARY_COLOR,
                }}
                onClick={() =>
                    setIsOpen(
                        previous =>
                            !previous
                    )
                }
            >
                <CalendarDays
                    size={18}
                    color={PRIMARY_COLOR}
                />

                <Text>
                    {displayValue ||
                        placeholder}
                </Text>
            </Button>

            {isOpen && (
                <Box
                    position="absolute"
                    zIndex={1000}
                    top="calc(100% + 8px)"
                    left={0}
                    width="310px"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="14px"
                    boxShadow="0 12px 30px rgba(0,0,0,0.12)"
                    p={4}
                >
                    <Flex
    align="center"
    justify="space-between"
    mb={4}
>
    <IconButton
        aria-label="Previous month"
        size="sm"
        variant="ghost"
        borderRadius="8px"
        onClick={() =>
            changeMonth(-1)
        }
    >
        <ChevronLeft size={18} />
    </IconButton>

    <Flex
        align="center"
        gap={1}
    >
        <Text
            fontWeight="600"
            fontSize="15px"
            color="gray.800"
        >
            {
                MONTHS[
                    visibleMonth.getMonth()
                ]
            }
        </Text>

        <select
            value={
                visibleMonth.getFullYear()
            }
            onChange={e => {
                const year =
                    Number(
                        e.target.value
                    );

                setVisibleMonth(
                    previous =>
                        new Date(
                            year,
                            previous.getMonth(),
                            1
                        )
                );
            }}
            style={{
                border: "none",
                background: "transparent",
                fontWeight: 600,
                fontSize: "15px",
                color: "#1F2937",
                cursor: "pointer",
                outline: "none",
            }}
        >
           {Array.from(
    {
        length:
            new Date().getFullYear() - 1960 + 1,
    },
    (_, index) => 1960 + index
).map(year => (
    <option
        key={year}
        value={year}
    >
        {year}
    </option>
))}
        </select>
    </Flex>

    <IconButton
        aria-label="Next month"
        size="sm"
        variant="ghost"
        borderRadius="8px"
        onClick={() =>
            changeMonth(1)
        }
    >
        <ChevronRight size={18} />
    </IconButton>
</Flex>

                    <Grid
                        templateColumns="repeat(7, 1fr)"
                        gap={1}
                        mb={2}
                    >
                        {WEEK_DAYS.map(
                            day => (
                                <GridItem
                                    key={day}
                                    textAlign="center"
                                >
                                    <Text
                                        fontSize="11px"
                                        fontWeight="600"
                                        color="gray.400"
                                        py={1}
                                    >
                                        {day}
                                    </Text>
                                </GridItem>
                            )
                        )}
                    </Grid>

                    <Grid
                        templateColumns="repeat(7, 1fr)"
                        gap={1}
                    >
                        {calendarDays.map(
                            ({
                                date,
                                currentMonth,
                            }) => {
                                const disabled =
                                    isDisabled(
                                        date
                                    );

                                const selected =
                                    isSameDay(
                                        date,
                                        selectedDate
                                    );

                                const isToday =
                                    isSameDay(
                                        date,
                                        today
                                    );

                                return (
                                    <GridItem
                                        key={date.toISOString()}
                                    >
                                        <Button
                                            type="button"
                                            width="36px"
                                            height="36px"
                                            minWidth="36px"
                                            mx="auto"
                                            borderRadius="9px"
                                            fontSize="13px"
                                            fontWeight={
                                                selected
                                                    ? "600"
                                                    : "400"
                                            }
                                            color={
                                                selected
                                                    ? "white"
                                                    : disabled
                                                    ? "gray.300"
                                                    : currentMonth
                                                    ? "gray.700"
                                                    : "gray.300"
                                            }
                                            bg={
                                                selected
                                                    ? PRIMARY_COLOR
                                                    : "transparent"
                                            }
                                            border={
                                                isToday &&
                                                !selected
                                                    ? "1px solid"
                                                    : "none"
                                            }
                                            borderColor={
                                                PRIMARY_COLOR
                                            }
                                            _hover={{
                                                bg:
                                                    !disabled &&
                                                    !selected
                                                        ? "blue.50"
                                                        : undefined,
                                            }}
                                            disabled={
                                                disabled
                                            }
                                            onClick={() =>
                                                handleSelect(
                                                    date
                                                )
                                            }
                                        >
                                            {
                                                date.getDate()
                                            }
                                        </Button>
                                    </GridItem>
                                );
                            }
                        )}
                    </Grid>

                    <Flex
                        justify="space-between"
                        align="center"
                        mt={4}
                        pt={3}
                        borderTop="1px solid"
                        borderColor="gray.100"
                    >
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            color={PRIMARY_COLOR}
                            onClick={() => {
                                onChange(
                                    formatDate(
                                        today
                                    )
                                );

                                setVisibleMonth(
                                    today
                                );

                                setIsOpen(
                                    false
                                );
                            }}
                        >
                            Today
                        </Button>

                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            color="gray.500"
                            onClick={() =>
                                setIsOpen(
                                    false
                                )
                            }
                        >
                            Cancel
                        </Button>
                    </Flex>
                </Box>
            )}
        </Box>
    );
};

export default MogomaDatePicker;